/**
 * Pinterest Ads — Sync Analytics HTTP lifecycle (Story 19.5, Task 1).
 *
 * Pinterest's analytics endpoints are **synchronous** (single GET per report
 * type, mirrors Google Ads' `searchStream`). Fundamentally different from
 * Microsoft Ads' Submit→Poll→Download lifecycle in `microsoft/reporting.ts`.
 *
 * The companion module `reports.ts` covers the **async** report templates
 * flow used when callers need >30 columns or pin-promotion-level
 * breakdowns — see the sync-vs-async decision tree in
 * `_bmad-output/implementation-artifacts/19-5-pinterest-ads-adapter.md`.
 *
 * Per-isolate memory ceiling: ≈30 MB peak at the 90-day cap (Story 19.5
 * Reviewer B). Future expansion to longer ranges requires streaming JSON
 * parse — not yet needed.
 *
 * @see https://developers.pinterest.com/docs/api/v5/ad_account-analytics/
 * @see https://developers.pinterest.com/docs/api/v5/campaigns-analytics/
 * @see https://developers.pinterest.com/docs/api/v5/ad_groups-analytics/
 * @see https://developers.pinterest.com/docs/api/v5/ads-analytics/
 * @see https://developers.pinterest.com/docs/api/v5/ad_targeting_analytics-get/
 */

import { safeFetch } from "../safe-fetch";
import { PINTEREST_API_BASE, buildPinterestHeaders } from "./api";
import { PinterestAdsApiCallError, parsePinterestAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

/** Re-export the API base from `api.ts` for parity with MS Ads' `MICROSOFT_ADS_REPORTING_API_BASE`. */
export const PINTEREST_ADS_API_BASE = PINTEREST_API_BASE;

/** Sync analytics calls are slower than discovery (some hit 10-15s server-side); use a longer timeout. */
export const PINTEREST_SYNC_TIMEOUT_MS = 30_000;

/** Pinterest's undocumented hard limit on `columns` per sync request (HTTP 400 `{ code: 6, message: "Columns limit exceeded" }`). */
export const PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST = 30 as const;

/** Pinterest enforces hourly granularity max 3 days per request (HTTP 400 if exceeded). */
export const MAX_HOURLY_GRANULARITY_DAYS = 3 as const;

/** Per-request cap on `campaign_ids` / `ad_group_ids` / `ad_ids` array params. */
export const PINTEREST_ADS_MAX_IDS_PER_REQUEST = 100 as const;

/** Per Pinterest v5 docs — sync `/analytics` accepts up to 90 days per request. */
export const MAX_SYNC_ANALYTICS_DAYS = 90 as const;

/** Per Pinterest v5 docs — async `/reports` accepts up to 185 days per request. */
export const MAX_ASYNC_REPORT_DAYS = 185 as const;

/**
 * Two metrics Pinterest's documentation flags as not-queryable via the sync
 * endpoints. Listed here as the single source of truth so `report-builders`
 * can assert at module load that no column list contains them.
 *
 * @see _bmad/bmm/data/integration-metrics/pinterest-ads.md (line 160)
 */
export const PINTEREST_ADS_NOT_QUERYABLE_SYNC = [
  "TOTAL_VIEW_LEAD",
  "TOTAL_ENGAGEMENT_LEAD",
] as const;

// ============================================================================
// Types
// ============================================================================

/**
 * The 8 sync report-type discriminators surfaced on every record's
 * `_reportType` field. Order is documentation-only — `Promise.all`
 * dispatches in parallel.
 *
 * Pipelines 1-4 hit dedicated `/analytics` endpoints. Pipelines 5-8 all hit
 * `/targeting_analytics` with distinct `targeting_types` query params (see
 * `report-builders.buildSyncAnalyticsRequest`).
 */
export type PinterestAdsReportType =
  | "overview"
  | "campaigns"
  | "adGroups"
  | "ads"
  | "keywords"
  | "ageGender"
  | "geo"
  | "interests";

/** Pinterest analytics granularity enum — matches Pinterest's API exactly. */
export type PinterestAdsGranularity =
  | "TOTAL"
  | "DAY"
  | "WEEK"
  | "MONTH"
  | "HOUR";

/** Per-attribution-window override values Pinterest accepts. */
export type PinterestAttributionWindowDays = 0 | 1 | 7 | 14 | 30 | 60;

/**
 * Optional attribution-window override surface. Defaults are `30 / 30 / 1`
 * per the integration-mapping directive (line 7) — match Pinterest's UI
 * defaults exactly so widgets reflect what users see in Pinterest's own
 * dashboard.
 */
export interface PinterestAttributionWindows {
  readonly clickWindowDays?: PinterestAttributionWindowDays;
  readonly engagementWindowDays?: PinterestAttributionWindowDays;
  readonly viewWindowDays?: PinterestAttributionWindowDays;
}

/**
 * Per-call options shared across every sync analytics endpoint. Date range
 * is always required; the rest mirror Pinterest's optional query params.
 */
export interface PinterestAdsAnalyticsOptions {
  readonly startDate: string; // YYYY-MM-DD (UTC-derived)
  readonly endDate: string;   // YYYY-MM-DD
  readonly columns: ReadonlyArray<string>;
  readonly granularity?: PinterestAdsGranularity;
  readonly attribution?: PinterestAttributionWindows;
  readonly conversionReportTime?: "TIME_OF_AD_ACTION" | "TIME_OF_CONVERSION";
}

/** Standard Pinterest analytics envelope: `{ data: [{ ... }] }`. */
export interface PinterestAdsAnalyticsResponse {
  readonly data?: ReadonlyArray<Record<string, unknown>>;
  readonly summary?: Record<string, unknown>;
}

// ============================================================================
// URL helpers
// ============================================================================

/** Compose query string with the columns/granularity/date/attribution params. */
function appendCommonAnalyticsParams(url: URL, options: PinterestAdsAnalyticsOptions): void {
  url.searchParams.set("start_date", options.startDate);
  url.searchParams.set("end_date", options.endDate);
  url.searchParams.set("columns", options.columns.join(","));
  url.searchParams.set("granularity", options.granularity ?? "DAY");
  if (options.attribution?.clickWindowDays !== undefined) {
    url.searchParams.set("click_window_days", String(options.attribution.clickWindowDays));
  }
  if (options.attribution?.engagementWindowDays !== undefined) {
    url.searchParams.set("engagement_window_days", String(options.attribution.engagementWindowDays));
  }
  if (options.attribution?.viewWindowDays !== undefined) {
    url.searchParams.set("view_window_days", String(options.attribution.viewWindowDays));
  }
  if (options.conversionReportTime !== undefined) {
    url.searchParams.set("conversion_report_time", options.conversionReportTime);
  }
}

/**
 * Validates the column count and the hourly granularity rule. These are the
 * two preconditions Pinterest 400s on; we surface a typed error early so
 * callers don't pay the round-trip.
 */
function validateAnalyticsOptions(options: PinterestAdsAnalyticsOptions): void {
  if (options.columns.length === 0) {
    throw new Error("Pinterest Ads analytics requires at least one column");
  }
  if (options.columns.length > PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST) {
    throw new Error(
      `Pinterest Ads sync analytics is limited to ${PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST} columns per request; got ${options.columns.length}`,
    );
  }
  if (options.granularity === "HOUR") {
    const days = countInclusiveDays(options.startDate, options.endDate);
    if (days > MAX_HOURLY_GRANULARITY_DAYS) {
      throw new Error(
        `Pinterest hourly granularity requires date range ≤ ${MAX_HOURLY_GRANULARITY_DAYS} days; got ${days}`,
      );
    }
  }
}

/** Inclusive day count for two YYYY-MM-DD strings; defensive against bad input. */
function countInclusiveDays(start: string, end: string): number {
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return 0;
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

/** Validate `id` lists against the per-request 100-item ceiling. */
function validateIds(ids: ReadonlyArray<string>, label: string): void {
  if (ids.length === 0) {
    throw new Error(`Pinterest Ads ${label} analytics requires at least one ID`);
  }
  if (ids.length > PINTEREST_ADS_MAX_IDS_PER_REQUEST) {
    throw new Error(
      `Pinterest Ads ${label} analytics is limited to ${PINTEREST_ADS_MAX_IDS_PER_REQUEST} IDs per request; got ${ids.length}`,
    );
  }
}

// ============================================================================
// Sync analytics fetchers
// ============================================================================

async function getJson(url: URL, accessToken: string, label: string): Promise<PinterestAdsAnalyticsResponse> {
  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildPinterestHeaders(accessToken) },
    PINTEREST_SYNC_TIMEOUT_MS,
  );
  if (!result.success) {
    throw buildAnalyticsApiError(result.error, label);
  }
  try {
    return (await result.data.json()) as PinterestAdsAnalyticsResponse;
  } catch {
    throw new PinterestAdsApiCallError(
      {
        type: "unknown",
        message: `Pinterest Ads ${label} response was not valid JSON`,
        status: null,
        retryable: false,
      },
      `Pinterest Ads ${label}`,
    );
  }
}

/** GET /v5/ad_accounts/{accountId}/analytics — account-level overview. */
export async function fetchPinterestAdsAccountAnalytics(
  accessToken: string,
  accountId: string,
  options: PinterestAdsAnalyticsOptions,
): Promise<PinterestAdsAnalyticsResponse> {
  validateAnalyticsOptions(options);
  const url = new URL(`${PINTEREST_ADS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/analytics`);
  appendCommonAnalyticsParams(url, options);
  return getJson(url, accessToken, "account analytics");
}

/** GET /v5/ad_accounts/{accountId}/campaigns/analytics — campaign breakdown. */
export async function fetchPinterestAdsCampaignsAnalytics(
  accessToken: string,
  accountId: string,
  campaignIds: ReadonlyArray<string>,
  options: PinterestAdsAnalyticsOptions,
): Promise<PinterestAdsAnalyticsResponse> {
  validateAnalyticsOptions(options);
  validateIds(campaignIds, "campaigns");
  const url = new URL(
    `${PINTEREST_ADS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/campaigns/analytics`,
  );
  url.searchParams.set("campaign_ids", campaignIds.join(","));
  appendCommonAnalyticsParams(url, options);
  return getJson(url, accessToken, "campaigns analytics");
}

/** GET /v5/ad_accounts/{accountId}/ad_groups/analytics — ad-group breakdown. */
export async function fetchPinterestAdsAdGroupsAnalytics(
  accessToken: string,
  accountId: string,
  adGroupIds: ReadonlyArray<string>,
  options: PinterestAdsAnalyticsOptions,
): Promise<PinterestAdsAnalyticsResponse> {
  validateAnalyticsOptions(options);
  validateIds(adGroupIds, "ad groups");
  const url = new URL(
    `${PINTEREST_ADS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/ad_groups/analytics`,
  );
  url.searchParams.set("ad_group_ids", adGroupIds.join(","));
  appendCommonAnalyticsParams(url, options);
  return getJson(url, accessToken, "ad groups analytics");
}

/** GET /v5/ad_accounts/{accountId}/ads/analytics — ad-level breakdown. */
export async function fetchPinterestAdsAdsAnalytics(
  accessToken: string,
  accountId: string,
  adIds: ReadonlyArray<string>,
  options: PinterestAdsAnalyticsOptions,
): Promise<PinterestAdsAnalyticsResponse> {
  validateAnalyticsOptions(options);
  validateIds(adIds, "ads");
  const url = new URL(
    `${PINTEREST_ADS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/ads/analytics`,
  );
  url.searchParams.set("ad_ids", adIds.join(","));
  appendCommonAnalyticsParams(url, options);
  return getJson(url, accessToken, "ads analytics");
}

/**
 * GET /v5/ad_accounts/{accountId}/targeting_analytics — multi-targeting-type
 * breakdown. Supports comma-joined `targeting_types` (e.g.
 * `KEYWORD,AGE_BUCKET_AND_GENDER`) per Pinterest's enum convention.
 */
export async function fetchPinterestAdsTargetingAnalytics(
  accessToken: string,
  accountId: string,
  targetingTypes: ReadonlyArray<string>,
  options: PinterestAdsAnalyticsOptions,
): Promise<PinterestAdsAnalyticsResponse> {
  validateAnalyticsOptions(options);
  if (targetingTypes.length === 0) {
    throw new Error("Pinterest Ads targeting_analytics requires at least one targeting_type");
  }
  const url = new URL(
    `${PINTEREST_ADS_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/targeting_analytics`,
  );
  url.searchParams.set("targeting_types", targetingTypes.join(","));
  appendCommonAnalyticsParams(url, options);
  return getJson(url, accessToken, "targeting analytics");
}

// ============================================================================
// Pure response normaliser
// ============================================================================

/**
 * Flattens Pinterest's analytics envelope into `Array<Record<string, unknown>>`.
 *
 * Pinterest returns:
 *   - `{ data: [{ DATE: "...", IMPRESSION_1: 100, ... }, ...] }` for time-series
 *     report types (DAY/WEEK/MONTH/HOUR granularity).
 *   - `{ summary: { IMPRESSION_1: 1000, ... } }` for `granularity=TOTAL` on
 *     some endpoints (account/targeting analytics).
 *   - `{ data: [...] }` even at TOTAL granularity for entity-scoped reports
 *     (campaigns / ad_groups / ads — one row per entity).
 *
 * This normaliser handles all three shapes. Returns `[]` for an empty/missing
 * envelope so non-fatal "no data" responses surface as empty arrays.
 *
 * @param json - Raw JSON envelope from any of the analytics endpoints
 * @param _reportType - For documentation; not used in flattening today, may
 *   later route to per-report-type extractors if Pinterest's envelope diverges.
 */
export function parsePinterestAdsSyncResponse(
  json: PinterestAdsAnalyticsResponse,
  _reportType: PinterestAdsReportType,
): Array<Record<string, unknown>> {
  if (Array.isArray(json.data)) {
    return json.data.map((row) => ({ ...row }));
  }
  if (json.summary && typeof json.summary === "object") {
    return [{ ...json.summary }];
  }
  return [];
}

// ============================================================================
// Internal: typed error builder shared by every analytics call
// ============================================================================

function buildAnalyticsApiError(
  safeFetchError: { message: string; providerMessage?: string; providerStatus?: number },
  label: string,
): PinterestAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON — fall back to status-only mapping
    }
  }
  const parsed = parsePinterestAdsApiError(parsedBody, safeFetchError.providerStatus);
  return new PinterestAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: safeFetchError.message,
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    `Pinterest Ads ${label}`,
  );
}
